import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import api from "@/lib/api";
import {
  Users, Pill, Package, AlertTriangle, Clock,
  Activity, ArrowRight, TrendingUp, CalendarDays,
  Sun, Moon, Sunset, BookOpen, Heart, MapPin
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

const shiftMeta = {
  manana: { label: "Mañana", icon: Sun, bg: "bg-amber-50", text: "text-amber-700", time: "06:00 - 14:00" },
  tarde: { label: "Tarde", icon: Sunset, bg: "bg-rose-50", text: "text-rose-600", time: "14:00 - 22:00" },
  noche: { label: "Noche", icon: Moon, bg: "bg-violet-50", text: "text-violet-700", time: "22:00 - 06:00" },
};

const severityColors = {
  leve: "bg-emerald-50 text-emerald-700",
  moderado: "bg-amber-50 text-amber-700",
  grave: "bg-rose-50 text-rose-600",
};

const typeLabels = {
  caida: "Caída", agresion: "Agresión", error_medicacion: "Error Med.",
  fuga: "Fuga", autolesion: "Autolesión", otro: "Otro",
};

export default function DashboardPage() {
  const { user, impersonatedTenant } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tenantShifts, setTenantShifts] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get("/dashboard/alerts"),
      api.get("/shifts-config"),
    ]).then(([dRes, sRes]) => {
      setData(dRes.data);
      setTenantShifts(sRes.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [impersonatedTenant]);

  if (loading) return <div className="flex items-center justify-center h-64"><p className="text-slate-400">Cargando dashboard...</p></div>;

  const stats = data?.stats || {};
  const alerts = data?.alerts || [];
  const currentShift = data?.current_shift || "manana";
  // Build dynamic shift info from tenant config, fallback to shiftMeta
  const dynamicShiftMeta = {};
  tenantShifts.forEach(s => {
    dynamicShiftMeta[s.key] = {
      label: s.label,
      icon: shiftMeta[s.key]?.icon || Sun,
      bg: shiftMeta[s.key]?.bg || "bg-slate-50",
      text: shiftMeta[s.key]?.text || "text-slate-700",
      time: `${s.start} - ${s.end}`,
    };
  });
  const shiftInfo = dynamicShiftMeta[currentShift] || shiftMeta[currentShift] || shiftMeta.manana;
  const ShiftIcon = shiftInfo.icon;
  const todayShifts = data?.today_shifts_detail || [];
  const latestLogs = data?.latest_logs || [];
  const recentIncidents = data?.recent_incidents || [];
  const vitalAlerts = data?.vital_alerts || [];
  const expiringStock = data?.expiring_stock || [];
  const currentShiftStaff = todayShifts.filter(s => s.shift_type === currentShift);

  const statCards = [
    { label: "Residentes", value: stats.total_residents, icon: Users, color: "bg-violet-50 text-violet-600" },
    { label: "Administraciones Hoy", value: stats.today_administrations, icon: Activity, color: "bg-emerald-50 text-emerald-600" },
    { label: "Incidentes Abiertos", value: stats.open_incidents, icon: AlertTriangle, color: stats.open_incidents > 0 ? "bg-rose-50 text-rose-500" : "bg-slate-50 text-slate-500" },
    { label: "Novedades Hoy", value: stats.today_logs, icon: BookOpen, color: "bg-amber-50 text-amber-600" },
  ];

  const quickActions = [
    { label: "Ronda de Medicación", to: "/medication-round", icon: Pill, desc: "Administrar medicación programada" },
    { label: "Libro de Novedades", to: "/daily-logs", icon: BookOpen, desc: "Registros del turno" },
    { label: "Reportar Incidente", to: "/incidents", icon: AlertTriangle, desc: "Reportar un nuevo incidente" },
  ];

  return (
    <div className="space-y-7 animate-fade-in-up" data-testid="dashboard-page">
      {/* Header with current shift */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-3 mb-1">
            {user?.role === "super_admin" && (
              <Badge className="rounded-full bg-violet-100 text-violet-700 border-0 text-[10px] font-bold uppercase tracking-wider">Super Admin</Badge>
            )}
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight font-[Manrope]">
            Buenos días, {user?.name?.split(" ")[0]}
          </h1>
          <p className="text-sm text-slate-400 mt-1">
            {new Date().toLocaleDateString("es-AR", { weekday: "long", year: "numeric", month: "long", day: "numeric" })}
          </p>
        </div>
        <Card className="rounded-2xl border-slate-100 shadow-sm sm:min-w-[220px]" data-testid="current-shift-card">
          <CardContent className="p-4 flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl ${shiftInfo.bg} flex items-center justify-center`}>
              <ShiftIcon className={`w-5 h-5 ${shiftInfo.text}`} strokeWidth={1.8} />
            </div>
            <div>
              <p className="text-[10px] text-slate-400 uppercase tracking-widest font-semibold">Turno Actual</p>
              <p className={`font-semibold text-sm ${shiftInfo.text}`}>{shiftInfo.label}</p>
              <p className="text-xs text-slate-400">{shiftInfo.time}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Alerts */}
      {alerts.length > 0 && (
        <div className="space-y-2">
          {alerts.map((alert) => {
            const resolveRoutes = {
              overdue_meds: "/medication-round",
              low_stock: "/stock",
              open_incidents: "/incidents",
              expiring_stock: "/stock",
            };
            const resolveTo = resolveRoutes[alert.id];
            return (
              <div
                key={alert.id}
                className={`rounded-2xl p-4 flex items-center gap-4 border ${
                  alert.type === "critical"
                    ? "bg-rose-50 border-rose-200"
                    : "bg-amber-50 border-amber-200"
                }`}
                data-testid={`alert-${alert.id}`}
              >
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                  alert.type === "critical" ? "bg-rose-100" : "bg-amber-100"
                }`}>
                  {alert.type === "critical"
                    ? <AlertTriangle className="w-5 h-5 text-rose-600 animate-pulse-soft" strokeWidth={2} />
                    : <Clock className="w-5 h-5 text-amber-600" strokeWidth={2} />
                  }
                </div>
                <div className="flex-1">
                  <p className={`font-semibold text-sm ${alert.type === "critical" ? "text-rose-700" : "text-amber-700"}`}>
                    {alert.title}
                  </p>
                  <p className={`text-sm ${alert.type === "critical" ? "text-rose-600/70" : "text-amber-600/70"}`}>
                    {alert.message}
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <Badge className={`rounded-full px-3 py-1 font-semibold text-xs ${
                    alert.type === "critical" ? "bg-rose-600 text-white" : "bg-amber-600 text-white"
                  }`}>
                    {alert.count}
                  </Badge>
                  {resolveTo && (
                    <Button
                      size="sm"
                      onClick={() => navigate(resolveTo)}
                      className={`rounded-xl h-8 px-3.5 text-xs font-semibold transition-all active:scale-[0.97] ${
                        alert.type === "critical"
                          ? "bg-rose-600 hover:bg-rose-700 text-white"
                          : "bg-amber-600 hover:bg-amber-700 text-white"
                      }`}
                      data-testid={`resolve-${alert.id}`}
                    >
                      Resolver <ArrowRight className="w-3 h-3 ml-1" />
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Stat Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat, i) => (
          <Card key={stat.label} className={`rounded-2xl border-slate-100 shadow-sm hover:shadow-md transition-all duration-200 hover:-translate-y-[1px]`}>
            <CardContent className="p-5">
              <div className={`w-10 h-10 rounded-xl ${stat.color} flex items-center justify-center mb-3`}>
                <stat.icon className="w-5 h-5" strokeWidth={1.8} />
              </div>
              <p className="text-2xl font-bold text-slate-900">{stat.value ?? 0}</p>
              <p className="text-xs text-slate-400 font-medium mt-0.5">{stat.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Quick Actions */}
      <div>
        <h2 className="text-base font-semibold text-slate-900 mb-3">Acciones Rápidas</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {quickActions.map((action) => (
            <Card
              key={action.to}
              className="rounded-2xl border-slate-100 shadow-sm cursor-pointer hover:shadow-md transition-all duration-200 hover:-translate-y-[1px] group"
              onClick={() => navigate(action.to)}
              data-testid={`quick-action-${action.label.toLowerCase().replace(/\s+/g, '-').replace(/ó/g, 'o')}`}
            >
              <CardContent className="p-5 flex items-center gap-4">
                <div className="w-10 h-10 rounded-xl bg-slate-50 flex items-center justify-center group-hover:bg-violet-50 transition-colors duration-200">
                  <action.icon className="w-5 h-5 text-slate-500 group-hover:text-violet-600 transition-colors duration-200" strokeWidth={1.8} />
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-slate-900 text-sm">{action.label}</p>
                  <p className="text-xs text-slate-400">{action.desc}</p>
                </div>
                <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-violet-500 transition-all duration-200 group-hover:translate-x-0.5" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Two Column: Staff on shift + Latest Logs */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Current Shift Staff */}
        <Card className="rounded-2xl border-slate-100 shadow-sm">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-slate-900 text-sm">Personal en Turno</h3>
              <Badge className={`rounded-full text-[10px] font-semibold border-0 px-2.5 ${shiftInfo.bg} ${shiftInfo.text}`}>
                {shiftInfo.label}
              </Badge>
            </div>
            {currentShiftStaff.length === 0 ? (
              <p className="text-sm text-slate-400 py-4 text-center">Sin personal asignado al turno actual</p>
            ) : (
              <div className="space-y-2">
                {currentShiftStaff.map(s => (
                  <div key={s.id} className="flex items-center gap-3 bg-slate-50 rounded-xl px-4 py-3" data-testid={`shift-staff-${s.id}`}>
                    <div className="w-8 h-8 rounded-full bg-violet-50 flex items-center justify-center text-violet-600 font-semibold text-xs">
                      {s.staff_name?.charAt(0)}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-900">{s.staff_name}</p>
                      <p className="text-xs text-slate-400">{s.role}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <Button
              variant="ghost"
              onClick={() => navigate("/staff")}
              className="mt-3 text-sm text-violet-600 font-medium px-0 hover:bg-transparent hover:underline"
            >
              Ver todos los turnos <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </CardContent>
        </Card>

        {/* Latest Logs */}
        <Card className="rounded-2xl border-slate-100 shadow-sm">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-slate-900 text-sm">Últimas Novedades</h3>
              <Badge className="rounded-full text-[10px] font-semibold border-0 px-2.5 bg-slate-100 text-slate-600">
                Hoy
              </Badge>
            </div>
            {latestLogs.length === 0 ? (
              <p className="text-sm text-slate-400 py-4 text-center">Sin novedades registradas hoy</p>
            ) : (
              <div className="space-y-2">
                {latestLogs.slice(0, 4).map(log => {
                  const sCfg = shiftMeta[log.shift_type] || shiftMeta.manana;
                  return (
                    <div key={log.id} className="bg-slate-50 rounded-xl px-4 py-3" data-testid={`dash-log-${log.id}`}>
                      <div className="flex items-center gap-2 mb-1">
                        <Badge className={`rounded-full text-[9px] font-semibold border-0 px-2 ${sCfg.bg} ${sCfg.text}`}>
                          {sCfg.label}
                        </Badge>
                        {log.priority !== "normal" && (
                          <Badge className={`rounded-full text-[9px] font-semibold border-0 px-2 ${log.priority === "urgente" ? "bg-rose-50 text-rose-600" : "bg-amber-50 text-amber-700"}`}>
                            {log.priority}
                          </Badge>
                        )}
                        <span className="text-[10px] text-slate-400">
                          {new Date(log.created_at).toLocaleTimeString("es-AR", { hour: "2-digit", minute: "2-digit" })}
                        </span>
                      </div>
                      <p className="text-sm text-slate-700 line-clamp-2">{log.content}</p>
                    </div>
                  );
                })}
              </div>
            )}
            <Button
              variant="ghost"
              onClick={() => navigate("/daily-logs")}
              className="mt-3 text-sm text-violet-600 font-medium px-0 hover:bg-transparent hover:underline"
            >
              Ver libro completo <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Bottom row: Incidents + Vitals + Stock */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Recent Incidents */}
        <Card className="rounded-2xl border-slate-100 shadow-sm">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-slate-900 text-sm">Incidentes Abiertos</h3>
              {recentIncidents.length > 0 && (
                <Badge className="rounded-full bg-rose-50 text-rose-600 border-0 text-[10px] font-semibold">
                  {recentIncidents.length}
                </Badge>
              )}
            </div>
            {recentIncidents.length === 0 ? (
              <p className="text-sm text-slate-400 py-4 text-center">Sin incidentes abiertos</p>
            ) : (
              <div className="space-y-2">
                {recentIncidents.slice(0, 3).map(inc => (
                  <div key={inc.id} className="bg-slate-50 rounded-xl px-4 py-3" data-testid={`dash-incident-${inc.id}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <Badge className={`rounded-full text-[9px] font-semibold border-0 px-2 ${severityColors[inc.severity] || severityColors.leve}`}>
                        {inc.severity}
                      </Badge>
                      <span className="text-[10px] text-slate-400">{typeLabels[inc.incident_type] || inc.incident_type}</span>
                    </div>
                    <p className="text-sm text-slate-700 line-clamp-1">{inc.resident_name || "—"}</p>
                  </div>
                ))}
              </div>
            )}
            <Button
              variant="ghost"
              onClick={() => navigate("/incidents")}
              className="mt-3 text-sm text-violet-600 font-medium px-0 hover:bg-transparent hover:underline"
            >
              Ver todos <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </CardContent>
        </Card>

        {/* Vital Alerts */}
        <Card className="rounded-2xl border-slate-100 shadow-sm">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-slate-900 text-sm">Alertas de Vitales</h3>
              <Heart className="w-4 h-4 text-slate-300" />
            </div>
            {vitalAlerts.length === 0 ? (
              <div className="py-4 text-center">
                <p className="text-sm text-emerald-600 font-medium">Todo en rango normal</p>
                <p className="text-xs text-slate-400 mt-1">Sin alertas de signos vitales hoy</p>
              </div>
            ) : (
              <div className="space-y-2">
                {vitalAlerts.slice(0, 3).map((va, i) => (
                  <div key={i} className="bg-rose-50 rounded-xl px-4 py-3 border border-rose-100">
                    <p className="text-sm font-medium text-rose-700">{va.resident_name}</p>
                    <p className="text-xs text-rose-600">{va.field}: <span className="font-bold">{va.value}</span> (rango: {va.range})</p>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Stock Summary */}
        <Card className="rounded-2xl border-slate-100 shadow-sm">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-slate-900 text-sm">Stock & Vencimientos</h3>
              <Package className="w-4 h-4 text-slate-300" />
            </div>
            <div className="flex items-center gap-3 mb-3">
              <p className="text-2xl font-bold text-slate-900">{stats.total_stock_items ?? 0}</p>
              <span className="text-sm text-slate-400">medicamentos</span>
            </div>
            {stats.low_stock_count > 0 && (
              <p className="text-sm text-rose-500 font-medium mb-1">
                {stats.low_stock_count} con stock bajo
              </p>
            )}
            {expiringStock.length > 0 && (
              <div className="mt-2 space-y-1">
                {expiringStock.slice(0, 2).map((es, i) => (
                  <p key={i} className="text-xs text-amber-600">
                    {es.name}: vence en {es.days_left <= 0 ? "VENCIDO" : `${es.days_left} días`}
                  </p>
                ))}
              </div>
            )}
            <Button
              variant="ghost"
              onClick={() => navigate("/stock")}
              className="mt-3 text-sm text-violet-600 font-medium px-0 hover:bg-transparent hover:underline"
            >
              Ver stock completo <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
